import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getCartStore } from '../api/cartStore';
import { BOOKING_SESSION_STORAGE_KEY, readStoredBookingSessionId } from '../lib/bookingSession';
import { bookingStateFromCart } from '../utils/cartCheckoutRestore';

const BookingFlowContext = createContext();

const STORAGE_KEY = BOOKING_SESSION_STORAGE_KEY;

export function BookingFlowProvider({ children }) {
  // The session id this page load started with, and whether it came from
  // storage. An id minted just now can't have a cart yet, so there is nothing
  // to rehydrate (and no reason to ask the server on every first visit).
  const [initialSession] = useState(() => {
    const stored = readStoredBookingSessionId();
    return { id: stored || generateSessionId(), restored: !!stored };
  });
  // CRITICAL: sessionId is now mutable to allow alignment with cart's session_id
  // This fixes the bug where context and cart would use different session IDs
  const [sessionId, setSessionId] = useState(initialSession.id);

  const [bookingState, setBookingState] = useState(getDefaultState());
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasLocalUpdates, setHasLocalUpdates] = useState(false);
  /** Session ids minted in this page load: they can't have a cart yet, so
   *  nothing asks the server for one. */
  const mintedSessionIdsRef = useRef(new Set(initialSession.restored ? [] : [initialSession.id]));

  // Rehydrate from Supabase on mount
  useEffect(() => {
    const rehydrateFromSupabase = async () => {
      if (mintedSessionIdsRef.current.has(sessionId)) {
        setIsHydrated(true);
        return;
      }
      try {
        // Signed in: the customer's own cart. Guest: /api/guest-cart.
        const store = await getCartStore();
        const { data, error } = await store.latestBySession(sessionId);

        if (error) {
          console.error('Failed to rehydrate from Supabase:', error);
          setIsHydrated(true);
          return;
        }

        if (data && !hasLocalUpdates) {
          console.log('✅ Rehydrated booking state from Supabase:', data);
          setBookingState(bookingStateFromCart(data));
        } else if (hasLocalUpdates) {
          console.log('⚠️ Skipping rehydration - local updates exist');
        }
        setIsHydrated(true);
      } catch (error) {
        console.error('Error rehydrating from Supabase:', error);
        setIsHydrated(true);
      }
    };

    rehydrateFromSupabase();
  }, [sessionId, hasLocalUpdates, initialSession]);

  // Sync to Supabase when booking state changes
  // NOTE: abandoned_trips table uses flight_data JSONB, not separate columns
  // Store selectedFlight, returnFlight, searchParams INSIDE flight_data
  useEffect(() => {
    if (!isHydrated) return; // Don't sync until after rehydration
    // Only local changes are written. State that was just READ from the cart
    // (a rehydration, a resume link) has nothing to add, and writing it back
    // reshaped the cart and bumped last_activity on a mere page load.
    if (!hasLocalUpdates) return;

    // CRITICAL: Only sync from inside the actual booking flow.
    // Previously this used a small blacklist of admin pages, which left every
    // other page (Layout, Profile, Dashboard, etc.) writing `last_activity`
    // any time the rehydrated bookingState was re-spread. That was the source
    // of "this cart says 1h ago but no one was using it" reports — a stale
    // bookingState in context syncing on an unrelated page.
    //
    // Allowlisting the booking-flow paths is the safer model: the cart's
    // last_activity now reflects only moments when the customer was actually
    // progressing through booking screens. Explicit events outside this
    // (credits applied, passengers entered, payment initiated) still update
    // last_activity directly via AbandonedTrip.update() in Review.jsx.
    const currentPath = window.location.pathname.toLowerCase();
    const bookingFlowPages = [
      '/search',
      '/googleflightssearch',
      '/flightoptions',
      '/selectseats',
      '/book',
      '/review',
      '/changeflightreview',
      '/reservecheckout',
      '/reserveswitchreview',
    ];
    if (!bookingFlowPages.some(page => currentPath.includes(page))) {
      return;
    }
    
    const syncToSupabase = async () => {
      try {
        const store = await getCartStore();
        const userId = store.userId;

        // Only sync if there's actual booking data
        if (bookingState.searchParams || bookingState.selectedFlight) {
          // Build merged flight_data with both selected and return flight info
          // This ensures all flight data is stored in the correct JSONB column
          
          // FIX: If returnFlight is null but selectedFlight has 2+ slices,
          // extract all return slices (after index 0) to preserve as _returnFlight
          let returnFlightToStore = bookingState.returnFlight;
          if (!returnFlightToStore && bookingState.selectedFlight?.slices?.length > 1) {
            const returnSlices = bookingState.selectedFlight.slices.slice(1);
            const hasValidSegments = returnSlices.some(slice => slice?.segments?.length > 0);
            
            if (hasValidSegments) {
              // Clone the full flight structure but with only return slices
              // This preserves all pricing, carrier, and offer metadata
              returnFlightToStore = {
                ...bookingState.selectedFlight,
                slices: returnSlices
              };
              // Remove internal keys that shouldn't be duplicated
              delete returnFlightToStore._returnFlight;
              delete returnFlightToStore._searchParams;
              
              console.log('🔄 Sync: Extracted return flight from merged slices for storage');
            }
          }
          
          const mergedFlightData = bookingState.selectedFlight ? {
            ...bookingState.selectedFlight,
            // Store return flight slices if available
            _returnFlight: returnFlightToStore || null,
            // Store search params for reference
            _searchParams: bookingState.searchParams || null,
            // CRITICAL: Persist the selected booking option (cabin/fare type)
            // This ensures "Main Select", "Comfort+", etc. are saved
            bookingOption: bookingState.bookingOption || null,
            // The customer opted out of Defera Zero on the booking-options page
            // and wants a deposit plan on this refundable fare. Review reads
            // this back (via choseRefundableInstallments) to NOT auto-upgrade
            // them to $0-down. It has to live on the cart: it used to exist
            // only in router state + React context, so any refresh, resume
            // link or session re-align silently reverted them to Defera Zero
            // (Collista Lewingdon, 2026-09-17).
            _chose_refundable_installments: bookingState.choseRefundableInstallments === true
          } : null;
          
          // CRITICAL: Only sync flight data, NOT passengers
          // Passengers are managed by Review.jsx's AbandonedTrip.update() and should NOT be overwritten here
          // Also avoid syncing booking_option and payment_method - they're stored in flight_data JSONB
          // NOTE: Status should always be 'in_cart' - only admin can change it manually
          const sessionData = {
            session_id: sessionId,
            user_id: userId,
            flight_data: mergedFlightData,
            status: 'in_cart',
            last_activity: new Date().toISOString()
          };

          // Use find-then-update-or-insert pattern to avoid PostgREST onConflict issues
          // First, check if a row exists for this session (the most recent one:
          // legacy duplicate session_ids exist)
          // CRITICAL: Fetch flight_data to preserve booking reference and payment fields
          const { data: existingRow, error: selectError } = await store.latestBySession(sessionId, 'id, flight_data');

          if (selectError) {
            console.error('❌ Failed to check existing session:', selectError);
            return;
          }

          if (existingRow) {
            // CRITICAL: Preserve booking reference and payment fields from existing flight_data
            // These are set by trackAbandonedTrip and must NOT be overwritten
            const existingFlightData = existingRow.flight_data || {};
            const sameFlight = !!existingFlightData.id && existingFlightData.id === sessionData.flight_data?.id;
            const preservedFields = {
              defera_booking_reference: existingFlightData.defera_booking_reference,
              _payment_method: existingFlightData._payment_method,
              _miles_to_deplete: existingFlightData._miles_to_deplete,
              // New state wins so a mid-cart opt-out actually lands; the
              // existing value is only the fallback. (Unlike _payment_method,
              // which this sync never carries, so preserving it is the point.)
              _chose_refundable_installments: sessionData.flight_data?._chose_refundable_installments
                ?? existingFlightData._chose_refundable_installments,
              // Only preserve existing bookingOption if we don't have a new one in
              // state, and only for the SAME flight: a fare kept from the flight
              // this cart held before is a fare for other flights (29% of carts
              // carried one, audit 2026-10-27).
              bookingOption: sessionData.flight_data?.bookingOption
                || (sameFlight ? existingFlightData.bookingOption : null),
              // The search the fare was priced for (its travelers). Review saves
              // it with the cart; a flow that never held it must not erase it.
              _searchParams: sessionData.flight_data?._searchParams
                || (sameFlight ? existingFlightData._searchParams ?? null : null)
            };
            
            // Merge: new flight data + preserved critical fields
            const mergedFlightDataWithPreserved = {
              ...sessionData.flight_data,
              ...preservedFields
            };
            
            // CRITICAL: Only update last_activity if flight_data actually changed.
            // The previous implementation used `JSON.stringify(obj, Object.keys(obj).sort())`
            // which only sorts top-level keys — any nested object whose key order
            // differs (which happens routinely when objects are spread/merged)
            // produced a different string, falsely registering as "changed" and
            // bumping last_activity. We now do a proper recursive stable stringify
            // so semantically equal objects always serialize identically.
            const stableStringify = (value) => {
              if (value === null || typeof value !== 'object') return JSON.stringify(value);
              if (Array.isArray(value)) {
                return '[' + value.map(stableStringify).join(',') + ']';
              }
              const keys = Object.keys(value).sort();
              return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
            };
            const existingFlightDataStr = stableStringify(existingFlightData);
            const newFlightDataStr = stableStringify(mergedFlightDataWithPreserved);
            const flightDataChanged = existingFlightDataStr !== newFlightDataStr;
            
            // Only include last_activity in update if flight data actually changed
            const updatePayload = {
              user_id: sessionData.user_id,
              flight_data: mergedFlightDataWithPreserved
            };
            
            // Only update last_activity when there's an actual change to flight data
            if (flightDataChanged) {
              updatePayload.last_activity = sessionData.last_activity;
            }
            
            // Row exists - update it
            const { error: updateError } = await store.updateById(existingRow.id, updatePayload);
            
            if (updateError) {
              console.error('❌ Failed to sync booking state to Supabase:', updateError);
            } else {
              console.log('✅ Synced booking state to Supabase (preserved booking reference:', preservedFields.defera_booking_reference, ')');
            }
          } else {
            // CRITICAL: Do NOT insert new carts here - let trackAbandonedTrip handle cart creation
            // This prevents race conditions where both BookingFlowContext and trackAbandonedTrip
            // try to insert at the same time, creating duplicate carts
            console.log('⏳ Skipping cart creation in BookingFlowContext (waiting for trackAbandonedTrip)');
          }
        }
      } catch (error) {
        console.error('❌ Error syncing to Supabase:', error);
      }
    };

    syncToSupabase();
    
    // Also keep sessionId in localStorage for quick access
    const lightweightData = { sessionId, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lightweightData));
  }, [sessionId, bookingState, isHydrated, hasLocalUpdates]);

  const updateBookingState = useCallback((updates) => {
    setHasLocalUpdates(true); // Mark that we have local updates to prevent rehydration overwrites
    setBookingState(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * A new, empty booking session (a new cart), for a newly opened flight.
   * Opening a different flight used to write it into the PREVIOUS session's
   * cart, which kept that cart's fare and bumped its last_activity: 29% of
   * carts ended up with a fare for other flights (audit 2026-10-27).
   * Returns the new session id.
   */
  const startNewSession = useCallback(() => {
    const newSessionId = generateSessionId();
    mintedSessionIdsRef.current.add(newSessionId);
    setHasLocalUpdates(false);
    setBookingState(getDefaultState());
    setSessionId(newSessionId);
    setIsHydrated(true);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId: newSessionId, timestamp: Date.now() }));
    return newSessionId;
  }, []);

  // CRITICAL: Allow external code to align session ID with cart's session_id
  // This prevents context from syncing to a different abandoned_trips row.
  // `cart` is that cart as the server already handed it over (the recovery
  // resume link). It fills an EMPTY flow only: signed out, this browser can no
  // longer read a cart the customer's account owns (migration 160).
  const alignSessionId = async (newSessionId, { cart: handedOverCart = null } = {}) => {
    if (newSessionId && newSessionId !== sessionId) {
      console.log('📍 BookingFlowContext: Aligning session ID:', sessionId, '→', newSessionId);
      
      // CRITICAL: Reset hasLocalUpdates to allow fresh rehydration from new session
      setHasLocalUpdates(false);
      setIsHydrated(false);
      
      // Update sessionId
      setSessionId(newSessionId);
      
      // Also update localStorage so next rehydration uses correct ID
      const lightweightData = { sessionId: newSessionId, timestamp: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lightweightData));
      
      // CRITICAL: Clear bookingState FIRST to prevent old cart data from syncing
      // This prevents stale data from being written to the new session
      setBookingState(getDefaultState());
      
      // Force rehydration from the new session
      try {
        const store = await getCartStore();
        const { data, error } = await store.latestBySession(newSessionId);

        if (data && !error) {
          console.log('✅ Rehydrated from aligned session:', newSessionId);
          setBookingState(bookingStateFromCart(data));
        } else {
          // No existing row for this session - state already cleared to default
          console.log('ℹ️ No existing data for aligned session:', newSessionId, '(starting fresh)');
        }
      } catch (err) {
        console.error('Error rehydrating from aligned session:', err);
        // State already cleared to default - no stale data can leak
      }

      setIsHydrated(true);
    }

    if (handedOverCart && handedOverCart.session_id === newSessionId) {
      setBookingState((prev) => (prev.selectedFlight ? prev : bookingStateFromCart(handedOverCart)));
    }
  };

  const clearBookingState = () => {
    setBookingState(getDefaultState());
    localStorage.removeItem(STORAGE_KEY);
  };

  const value = {
    sessionId,
    ...bookingState,
    /** Whether the Supabase rehydration has settled. Consumers that REDIRECT on
     *  missing state must wait for this — the cart lives in `abandoned_trips`
     *  and arrives one round-trip after mount, so anything reading
     *  `selectedFlight` on first render sees null even when a cart exists.
     *  Review.jsx bounced refreshing customers to /search for exactly that
     *  reason (its own comment said context was the refresh fallback). */
    isHydrated,
    updateBookingState,
    clearBookingState,
    alignSessionId,
    startNewSession
  };

  return (
    <BookingFlowContext.Provider value={value}>
      {children}
    </BookingFlowContext.Provider>
  );
}

export function useBookingFlow() {
  const context = useContext(BookingFlowContext);
  if (!context) {
    throw new Error('useBookingFlow must be used within BookingFlowProvider');
  }
  return context;
}

function generateSessionId() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function getDefaultState() {
  return {
    searchParams: null,
    selectedFlight: null,
    returnFlight: null,
    bookingOption: null,
    passengers: [],
    paymentMethod: 'installments',
    choseRefundableInstallments: false
  };
}
